import { createCacheWrapper } from '@utils/cache';
import { DAYS_TO_MS, HOURS_TO_MS } from '@utils/constants';
import { userAreaRootDescriptor } from './descriptor.user-area.js';
import { getBreadcrumbs } from './request.get-breadcrumbs.js';
import { getNestedPages } from './request.get-nested-pages.js';
import { getResourcesForPage } from './request.get-resources-for-page.js';
import { extractElementIdFromPath } from './util.get-element-id-from-path.js';

globalThis.structuredClone ??= (it) => JSON.parse(JSON.stringify(it));
const cache = createCacheWrapper({
  timeToLiveMs: 1 * HOURS_TO_MS,
  evictAfterMs: 1 * DAYS_TO_MS
});
function getPartialSitemap(portalConfig, path, options) {
  const cacheKey = [
    portalConfig.id,
    portalConfig.homePath,
    portalConfig.searchPath,
    path,
    !!(options == null ? void 0 : options.previewMode)
  ].join(":");
  return cache((options == null ? void 0 : options.disableCache) ? cacheKey : void 0, async () => {
    var _a;
    if (extractElementIdFromPath(path) !== void 0) {
      return {
        rootDescriptors: await resolveRootDescriptors(portalConfig, options),
        searchPath: portalConfig.searchPath
      };
    }
    const [breadcrumbs, rootDescriptors] = await Promise.all([
      getBreadcrumbs(portalConfig, path, options),
      structuredClone(await resolveRootDescriptors(portalConfig, options))
    ]);
    if (!breadcrumbs) {
      return {
        rootDescriptors,
        searchPath: portalConfig.searchPath,
        notFoundPaths: [path]
      };
    }
    const navigationLayers = await Promise.all(
      breadcrumbs.map(async (breadcrumb) => ({
        parentId: breadcrumb,
        nestedPages: structuredClone(
          await getNestedPages(portalConfig, breadcrumb, options)
        )
      }))
    );
    let subNavigationRoot;
    let parentLayer = rootDescriptors;
    for (const navigationLayer of navigationLayers) {
      const parentPageDescriptor = parentLayer.find(
        (it) => it.id === navigationLayer.parentId
      );
      if (parentPageDescriptor === void 0) {
        break;
      }
      if (parentPageDescriptor.type === "Course") {
        subNavigationRoot = parentPageDescriptor;
      }
      parentPageDescriptor.children = navigationLayer.nestedPages;
      parentLayer = navigationLayer.nestedPages;
    }
    subNavigationRoot = subNavigationRoot ?? parentLayer.find((it) => it.path === path);
    if (subNavigationRoot == null ? void 0 : subNavigationRoot.hasChildren) {
      subNavigationRoot.children = structuredClone(
        await getNestedPages(portalConfig, subNavigationRoot.id, {
          ...options,
          levels: subNavigationRoot.type === "Course" ? 2 : 1
        })
      );
    }
    const pageDescriptorsWithResources = [];
    if (subNavigationRoot == null ? void 0 : subNavigationRoot.hasResources) {
      pageDescriptorsWithResources.push(subNavigationRoot);
    }
    if ((subNavigationRoot == null ? void 0 : subNavigationRoot.type) === "Course") {
      for (let nestedPageDescriptor = subNavigationRoot; nestedPageDescriptor = (_a = nestedPageDescriptor.children) == null ? void 0 : _a.find(
        (it) => path.indexOf(it.path) === 0
      ); ) {
        if (nestedPageDescriptor.hasResources) {
          pageDescriptorsWithResources.push(nestedPageDescriptor);
        }
      }
    }
    await Promise.all(
      pageDescriptorsWithResources.map(async (pageDescriptor) => {
        pageDescriptor.resources = await getResourcesForPage(
          portalConfig.id,
          pageDescriptor.id,
          options
        );
      })
    );
    const requestedPage = findInSitemap(
      rootDescriptors,
      (it) => it.path === path.replace(/^\?(.*)$/, "")
    );
    return requestedPage !== void 0 ? { rootDescriptors, searchPath: portalConfig.searchPath } : {
      rootDescriptors,
      searchPath: portalConfig.searchPath,
      notFoundPaths: [path]
    };
  }).getStaleWhileRevalidate();
}
function findInSitemap(sitemap, equalsFn) {
  for (const item of sitemap) {
    if (equalsFn(item)) {
      return item;
    }
    if (item.children !== void 0) {
      const subitem = findInSitemap(item.children, equalsFn);
      if (subitem !== void 0) {
        return subitem;
      }
    }
  }
}
async function resolveRootDescriptors(portalConfig, options) {
  const baseRootDescriptors = await getNestedPages(
    portalConfig,
    void 0,
    options
  );
  return [...baseRootDescriptors, userAreaRootDescriptor];
}

export { findInSitemap, getPartialSitemap };
//# sourceMappingURL=request.get-partial-sitemap.js.map
